import React, { useState, useEffect, useMemo } from "react";
import ManagementList from "../components/ManagementList";
import TransactionModal from "../components/TransactionModal"; // Import Transaction Modal
import {
  fetchCustomerList,
  deleteCustomer,
  getDeviceDeeplink,
  deactivateCustomer,
  syncSimTelco,
} from "../services/api";
import { getCurrentUser } from "../services/authService";
import { OverlayTrigger, Tooltip, Modal, Button } from "react-bootstrap";
import { InfoCircle } from "react-bootstrap-icons";
import { LIMIT } from "../lib/utils/constant";
import DeviceSettings from "../components/DeviceSettings/DeviceSettings";
import { usePermissions } from "../Hooks/usePermission";
import { PERMISSIONS } from "../lib/utils/permissions";
import PermissionMessage from "../components/ErrorMessage/PermissionMessage";
function CustomerManagement() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- Modal State for Sync ---
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncMessage, setSyncMessage] = useState("");
  const [syncStatus, setSyncStatus] = useState("success"); // 'success' or 'error'
  const [showDeactivateResultModal, setShowDeactivateResultModal] =
    useState(false);
  const [deactivateMessage, setDeactivateMessage] = useState("");
  const [deactivateStatus, setDeactivateStatus] = useState("success");

  // --- Modal State for Transactions ---
  const [showTxnModal, setShowTxnModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // --- Modal State for Settings (default local UI + optional deeplink iframe) ---
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsIframeUrl, setSettingsIframeUrl] = useState("");
  const [showSettingsIframe, setShowSettingsIframe] = useState(false);
  const [settingsIframeLoading, setSettingsIframeLoading] = useState(false);
  const [selectedSettingsDeviceId, setSelectedSettingsDeviceId] =
    useState(null);
  const [selectedSettingsUserName, setSelectedSettingsUserName] = useState("");
  const [selectedSettingsSimNumber, setSelectedSettingsSimNumber] =
    useState("");
  const [selectedSettingsCaseManagerName, setSelectedSettingsCaseManagerName] =
    useState("");
  const [selectedSettingsCaseManagerAccessMode, setSelectedSettingsCaseManagerAccessMode] =
    useState(null);

  const closeSettingsModal = () => {
    setShowSettingsModal(false);
    setSettingsIframeUrl("");
    setShowSettingsIframe(false);
    setSettingsIframeLoading(false);
    setSelectedSettingsDeviceId(null);
    setSelectedSettingsUserName("");
    setSelectedSettingsSimNumber("");
    setSelectedSettingsCaseManagerName("");
    setSelectedSettingsCaseManagerAccessMode(null);
  };

  // --- Get Current User for Role Check ---
  const currentUser = getCurrentUser();
  // Check if user is Super Admin (adjust 'SYS_ADMIN' based on your actual API role code)
  const isSuperAdmin =
    currentUser?.role?.code === "SYS_ADMIN" ||
    currentUser?.role?.code === "SUPER_ADMIN";
  // --- Permissions: Check if standard User ---
  const isStandardUser = currentUser?.role?.code === "USER";
  const canEdit = !isStandardUser; // Only non-USER roles can edit/add/deactivate

  // --- Dynamic Columns Logic ---
  const getColumns = () => {
    // Base columns
    let cols = [
      {
        header: "ID",
        accessor: "idLink",
        searchKey: "record_id",
        sortable: true,
        sortKey: "id",
        render: (item) => {
          const isIdClickable =
            statusFilter !== "D" && statusFilter !== "P" && canViewAlerts;
          if (!isIdClickable) {
            return (
              <span className="text-primary fw-bold">{item.recordId}</span>
            );
          }
          return (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>View Device User Details</Tooltip>}
            >
              <span
                className="text-primary fw-bold text-decoration-underline"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  if (!item.deviceDbId) return;
                  handleSettingsClick(item);
                }}
              >
                {item.recordId}
              </span>
            </OverlayTrigger>
          );
        },
      },
      {
        header: "ENROLLMENT DATE",
        accessor: "enrollmentDate",
        sortable: true,
        sortKey: "raw_enrollmentDate",
        minWidth: "130px",
      },
      {
        header: "NAME",
        accessor: "fullName",
        searchKey: "name",
        sortable: true,
        sortKey: "firstName",
        minWidth: "160px",
      },
      {
        header: "EMAIL",
        accessor: "email",
        searchKey: "email",
        sortable: true,
        sortKey: "email",
        minWidth: "200px",
      },
      {
        header: "IMEI",
        accessor: "deviceImei",
        searchKey: "imei",
        sortable: true,
        sortKey: "deviceImei",
        minWidth: "155px",
      },
      {
        header: "ICCID",
        accessor: "iccidDisplay",
        sortable: true,
        sortKey: "raw_iccid",
        minWidth: "140px",
      },
      {
        header: "SIM NUMBER",
        accessor: "msisdn",
        searchKey: "mo",
        sortable: true,
        sortKey: "raw_msisdn",
        minWidth: "120px",
      },
      { header: "SIM STATUS", accessor: "simStatus", minWidth: "100px" },
      ...(isSuperAdmin
        ? [
            {
              header: "CLIENT",
              accessor: "clientName",
              searchKey: "client_name",
              sortable: true,
              sortKey: "clientName",
              minWidth: "120px",
            },
          ]
        : []),
      {
        header: "Case Manager",
        accessor: "assignedCaseManagerName",
        searchKey: "case_manager_name",
        hidden: true,
      },
    ];

    // Only show ACTION column if user has permission
    if (canEdit) {
      cols.push({ header: "ACTION", accessor: "action", align: "center" });
    }

    // Remove Action and IMEI columns if Deactivated
    if (statusFilter === "D") {
      cols = cols.filter(
        (col) =>
          col.accessor !== "action" &&
          col.accessor !== "deviceImei" &&
          col.accessor !== "msisdn" &&
          col.accessor !== "iccidDisplay" &&
          col.accessor !== "simStatus",
      );
      // Insert Deactivated At and Reason
      // Inserting after Enrollment Date (index 1)
      cols.push({
        header: "DEACTIVATION DATE",
        accessor: "deactivatedAt",
        sortable: true,
        sortKey: "raw_deactivatedAt",
      });

      // Add Reason at the end
      cols.push({
        header: "DEACTIVATION REASON",
        accessor: "deactivationReason",
        align: "center",
      });
    }

    return cols;
  };

  // --- API State ---
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(LIMIT[0].value);
  const [statusFilter, setStatusFilter] = useState("A");
  const [counts, setCounts] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchIn, setSearchIn] = useState(["record_id", "name"]);
  // --- Sorting State ---
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [bulkAssignPatients, setBulkAssignPatients] = useState([]);

  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);

  const [dateRange, setDateRange] = useState({
    start: thirtyDaysAgo.toISOString(),
    end: today.toISOString(),
  });
  const { can } = usePermissions();
  const userRoleCode = JSON.parse(localStorage.getItem("user"))?.role?.code;
  const canViewCustomer = can(PERMISSIONS.CUSTOMER_READ, userRoleCode);
  const canCreateCustomer = can(PERMISSIONS.CUSTOMER_ONBOARD, userRoleCode);
  const canUpdateCustomer = can(
    PERMISSIONS.CUSTOMER_PROFILE_UPDATE,
    userRoleCode,
  );
  const canDeactivateCustomer = can(
    PERMISSIONS.CUSTOMER_DEACTIVATE,
    userRoleCode,
  );
  const canViewTransactions = can(PERMISSIONS.RECEIPT_READ, userRoleCode);
  const canDownloadInvoice = can(PERMISSIONS.RECEIPT_DOWNLOAD, userRoleCode);
  const canViewAlerts = can(PERMISSIONS.ALERTS_VIEW, userRoleCode);
  // --- Logic: Open Device settings modal ---
  const handleSettingsClick = (row) => {
    const deviceDbId = row?.deviceDbId;
    if (!deviceDbId) {
      alert("No device associated with this device user.");
      return;
    }
    const rowName = row?.fullName || row?.name || row?.full_name || "";
    const simNumber =
      row?.raw_msisdn ||
      (row?.msisdn && row.msisdn !== "-" ? row.msisdn : "") ||
      "";
    setSelectedSettingsDeviceId(deviceDbId);
    setSelectedSettingsUserName(rowName);
    setSelectedSettingsSimNumber(simNumber);
    setSelectedSettingsCaseManagerName(row?.assignedCaseManagerName || "");
    setSelectedSettingsCaseManagerAccessMode(row?.caseManagerAccessMode || null);
    setSettingsIframeUrl("");
    setShowSettingsIframe(false);
    setShowSettingsModal(true);
  };

  // --- Logic: Toggle between local settings and deeplink iframe ---
  const handleSettingsDetailsToggle = async () => {
    if (showSettingsIframe) {
      setShowSettingsIframe(false);
      setSettingsIframeUrl("");
      return;
    }

    if (settingsIframeUrl) {
      setShowSettingsIframe(true);
      return;
    }

    if (!selectedSettingsDeviceId) {
      alert("No device associated with this device user.");
      return;
    }

    setSettingsIframeLoading(true);
    try {
      const response = await getDeviceDeeplink(selectedSettingsDeviceId);
      if (
        response.data &&
        response.data.success &&
        response.data.data?.redirectUrl
      ) {
        setSettingsIframeUrl(response.data.data.redirectUrl);
        setShowSettingsIframe(true);
      } else {
        alert("Could not generate settings link.");
      }
    } catch (err) {
      console.error("Error fetching deeplink:", err);
      alert("Failed to open settings.");
    } finally {
      setSettingsIframeLoading(false);
    }
  };

  // --- Helper: Map SIM Status Code to Display Text & Color ---
  const getSimStatusDisplay = (status) => {
    if (!status) return { text: "-", color: "inherit" };

    switch (status) {
      case "ACTIVE":
        return { text: "Active", color: "#198754" }; // Green

      case "ACTIVATION_REQUESTED":
      case "DEACTIVATION_REQUESTED":
      case "PENDING_STATUS_VERIFICATION":
      case "STATUS_VERIFICATION_FAILED":
      case "ACTIVATION_FAILED_TEMP":
      case "DEACTIVATION_FAILED_TEMP":
        return { text: "In Progress", color: "#ffc107" }; // Yellow/Warning (or blue if preferred)

      case "DEACTIVATED":
        return { text: "Inactive", color: "#dc3545" }; // Grey

      case "ACTIVATION_FAILED":
      case "DEACTIVATION_FAILED":
        return { text: "Process Failed", color: "#dc3545" }; // Red

      default:
        return { text: status, color: "inherit" };
    }
  };

  const handleLimitChange = (e) => {
    setItemsPerPage(e.target.value);
  };

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const payload = {
        start: null,
        end: null,
        page: page,
        limit: itemsPerPage,
        status: statusFilter,
        search: searchTerm.trim() || null,
        searchIn: searchIn.length > 0 ? searchIn : null,
      };

      const response = await fetchCustomerList(payload);

      if (response.data && response.data.success && response.data.data) {
        const apiData = response.data.data;

        const mappedCustomers = (apiData.items || []).map((c) => {
          // Extract Device & SIM info
          const deviceObj =
            c.devices && c.devices.length > 0 ? c.devices[0] : null;
          const simObj = deviceObj ? deviceObj.currentSim : null;
          const statusInfo = getSimStatusDisplay(simObj?.status);

          return {
            id: c.id,
            recordId: c.record_id,
            idLink: null,
            fullName: `${c.first_name || ""} ${c.last_name || ""}`,
            firstName: c.first_name,
            lastName: c.last_name,
            email: c.email,
            clientName: c.entity_master?.name || "N/A",
            entityMasterId: c.entity_master?.id ?? null,
            assignedCaseManagerName: c.case_manager
              ? `${c.case_manager.firstName || ""} ${c.case_manager.lastName || ""}`.trim() ||
                null
              : null,
            assignedCaseManagerId: c.case_manager?.id ?? null,
            caseManagerAccessMode:
              c.entity_master?.caseManagerAccessMode ||
              c.caseManagerAccessMode ||
              null,
            // Formatted Dates (using snake_case fields from new API)
            enrollmentDate: c.enrollment_ts
              ? new Date(c.enrollment_ts).toLocaleDateString("en-US")
              : "-",
            deactivatedAt: c.deactivated_at
              ? new Date(c.deactivated_at).toLocaleDateString("en-US")
              : "-",
            // Raw Values for Sorting
            raw_enrollmentDate: c.enrollment_ts,
            raw_deactivatedAt: c.deactivated_at,
            // Device Info (Assuming backend returns these fields, otherwise placeholders)
            // Device Info
            deviceDbId: deviceObj?.id || null,
            deviceImei: deviceObj?.imei || "-",

            // SIM Info (MSISDN)
            msisdn: simObj?.msisdn || "-",
            raw_msisdn: simObj?.msisdn || "",
            simId: simObj?.id, // Store SIM ID for syncing

            // ICCID with Status Dot
            iccidDisplay: (
              <div className="d-flex align-items-center">
                {simObj?.iccid ? (
                  <>
                    {/* <span
                      className="me-2"
                      style={{
                        height: '10px',
                        width: '10px',
                        backgroundColor: simObj.status === 'A' ? '#198754' : '#dc3545', // Green : Red
                        borderRadius: '50%',
                        display: 'inline-block'
                      }}
                      title={simObj.status === 'A' ? 'Active' : 'Inactive'}
                    ></span> */}
                    {simObj.iccid}
                  </>
                ) : (
                  "-"
                )}
              </div>
            ),
            // SIM STATUS COLUMN
            simStatus: (
              <span
                style={{
                  color: statusInfo.color,
                  fontWeight: "bold",
                  fontSize: "0.9rem",
                }}
              >
                {statusInfo.text}
              </span>
            ),
            raw_iccid: simObj?.iccid || "",
            deactivationReason: c.deactivation_reason || "-",
          };
        });

        setCustomers(mappedCustomers);
        setTotalPages(apiData.pagination?.pages || 1);
        setCounts(apiData.counts);
      } else {
        setCustomers([]);
        setTotalPages(1);
        setCounts(null);
      }
    } catch (error) {
      console.error("Error loading customers:", error);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, [page, statusFilter, dateRange, itemsPerPage, searchTerm, searchIn]);

  const handleBulkAssignCaseManager = (selectedPatients) => {
    setBulkAssignPatients(selectedPatients);
  };

  const handleDelete = async (id) => {
    try {
      await deleteCustomer(id);
      loadCustomers();
    } catch (err) {
      console.error("Error deleting customer:", err);
    }
  };

  // --- Updated Deactivate Handler ---
  const handleDeactivate = async (id, reason) => {
    try {
      const response = await deactivateCustomer(id, reason);
      setDeactivateMessage(
        response?.data?.message || "Device user deactivated successfully.",
      );
      setDeactivateStatus("success");
      setShowDeactivateResultModal(true);
      await loadCustomers();
      return true;
    } catch (err) {
      console.error("Error deactivating customer:", err);
      setDeactivateMessage(
        err?.response?.data?.message || "Failed to deactivate device user.",
      );
      setDeactivateStatus("error");
      setShowDeactivateResultModal(true);
      return false;
    }
  };

  // --- View Transactions Handler ---
  const handleViewTransactions = (item) => {
    // Ensure we have a customer ID (either mapped id or original ID)
    const cId = item.id;
    const cName = item.fullName || "Customer";
    setSelectedCustomer({ id: cId, name: cName });
    setShowTxnModal(true);
  };

  const handleTabChange = (tabKey) => {
    let apiStatus = "A";
    if (tabKey === "active") apiStatus = "A";
    if (tabKey === "pending") apiStatus = "P";
    if (tabKey === "deactivated") apiStatus = "D";
    if (tabKey === "all") apiStatus = "ALL";

    setStatusFilter(apiStatus);
    setPage(1);
    setSortConfig({ key: null, direction: "asc" });
  };

  const handleSearch = (term, selectedFields) => {
    setSearchTerm(term);
    if (Array.isArray(selectedFields) && selectedFields.length > 0) {
      setSearchIn(selectedFields);
    }
    setPage(1);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // --- Client-Side Sorting ---
  const sortedCustomers = useMemo(() => {
    let sortableItems = [...customers];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle nulls/undefined for strings/numbers to avoid crash
        if (aValue === undefined || aValue === null) aValue = "";
        if (bValue === undefined || bValue === null) bValue = "";

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [customers, sortConfig]);

  // --- NEW: Sync SIM Handler ---
  const handleSyncSim = async (item) => {
    if (!item.simId) {
      setSyncMessage("No SIM assigned to this user.");
      setSyncStatus("error");
      setShowSyncModal(true);
      return;
    }

    try {
      await syncSimTelco(item.simId);
      setSyncMessage("SIM synced successfully!");
      setSyncStatus("success");
      setShowSyncModal(true);
      loadCustomers();
    } catch (err) {
      setSyncMessage(err.response?.data?.message || "Failed to sync SIM.");
      setSyncStatus("error");
      setShowSyncModal(true);
    }
  };
  return (
    <>
      {canViewCustomer ? (
        <ManagementList
          title="Device Users"
          data={sortedCustomers}
          columns={getColumns()}
          // Only pass actions if canEdit is true
          addNewLink={canEdit ? "/customers/new" : null}
          editLinkBase={canEdit ? "/customers/edit" : null}
          onDeactivate={canEdit ? handleDeactivate : null}
          onSync={canEdit ? handleSyncSim : null}
          onViewTransactions={handleViewTransactions} // <-- Pass this prop
          // onSettings={handleSettingsClick}
          isServerSide={true}
          isLoading={loading}
          serverPage={page}
          serverTotalPages={totalPages}
          serverCounts={counts}
          onPageChange={(newPage) => setPage(newPage)}
          onTabChange={handleTabChange}
          onSearch={handleSearch}
          onSort={handleSort}
          sortConfig={sortConfig}
          limit={itemsPerPage}
          handleLimitChange={handleLimitChange}
          showSearchFilterBtn={true}
          showSearchBar={false}
          deactivationType="device user"
          canCreate={canCreateCustomer}
          canUpdate={canUpdateCustomer}
          canDeactivate={canDeactivateCustomer}
          canViewTransactions={canViewTransactions}
          isAdminDeactivateDisabled={() => {
            return false;
          }}
          onBulkAssignCaseManager={
            canEdit && statusFilter !== 'D' ? handleBulkAssignCaseManager : undefined
          }
          bulkGroupByField="clientName"
        />
      ) : (
        <PermissionMessage />
      )}
      {/* --- Sync Result Modal --- */}
      <Modal
        show={showSyncModal}
        onHide={() => setShowSyncModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title
            className={
              syncStatus === "success" ? "text-success" : "text-danger"
            }
          >
            {syncStatus === "success" ? "Success" : "Error"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{syncMessage}</Modal.Body>
        <Modal.Footer>
          <Button
            variant={syncStatus === "success" ? "success" : "secondary"}
            onClick={() => setShowSyncModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeactivateResultModal}
        onHide={() => setShowDeactivateResultModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title
            className={
              deactivateStatus === "success" ? "text-success" : "text-danger"
            }
          >
            {deactivateStatus === "success" ? "Success" : "Error"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{deactivateMessage}</Modal.Body>
        <Modal.Footer>
          <Button
            variant={deactivateStatus === "success" ? "success" : "secondary"}
            onClick={() => setShowDeactivateResultModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* --- Device settings (deeplink in iframe) --- */}
      <Modal
        show={showSettingsModal}
        onHide={closeSettingsModal}
        size="xl"
        dialogClassName="modal-device-settings-dialog"
        fullscreen="lg-down"
        centered
      >
        <Modal.Body className="p-0 device-settings-modal__body">
          <button
            type="button"
            className="device-settings-modal__floating-close"
            onClick={closeSettingsModal}
            aria-label="Close device settings"
          >
            ×
          </button>
          <div className="device-settings-modal__info-banner">
            <InfoCircle
              size={18}
              className="device-settings-modal__info-icon"
            />
            <p className="mb-0" style={{ fontSize: "16px" }}>
              {settingsIframeLoading
                ? "Loading detailed view..."
                : showSettingsIframe
                  ? "Current and historical details are visible. To switch back to the past 24 hours view "
                  : "The device information displayed below is limited to the past 24 hours. To access current and historical data "}
              <button
                type="button"
                className="device-settings-modal__info-link device-settings-modal__info-link--cta"
                onClick={handleSettingsDetailsToggle}
                disabled={settingsIframeLoading}
              >
                Click here
              </button>
            </p>
          </div>
          <div className="device-settings-modal__content">
            {showSettingsIframe && settingsIframeUrl ? (
              <iframe
                key={settingsIframeUrl}
                title="Device settings"
                src={settingsIframeUrl}
                className="w-100 h-100 border-0 d-block"
                referrerPolicy="strict-origin-when-cross-origin"
              />
            ) : (
              <DeviceSettings
                deviceId={selectedSettingsDeviceId}
                fallbackFullName={selectedSettingsUserName}
                fallbackSimNumber={selectedSettingsSimNumber}
                caseManagerAccessMode={selectedSettingsCaseManagerAccessMode}
                caseManagerName={selectedSettingsCaseManagerName}
              />
            )}
          </div>
        </Modal.Body>
      </Modal>

      {/* --- Transaction History Modal --- */}
      {showTxnModal && selectedCustomer && (
        <TransactionModal
          show={showTxnModal}
          onHide={() => setShowTxnModal(false)}
          customerId={selectedCustomer.id}
          customerName={selectedCustomer.name}
          canDownloadInvoice={canDownloadInvoice}
        />
      )}
    </>
  );
}

export default CustomerManagement;
